import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  FileText,
  Image,
  Upload,
  Loader2,
  BarChart3,
  Share2,
  Eye,
  Check,
  AlertCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMarketingPanel = () => {
  const [activeTab, setActiveTab] = useState("content");
  const [categories, setCategories] = useState({});
  const [resourceCategories, setResourceCategories] = useState({});
  const [content, setContent] = useState([]);
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showContentModal, setShowContentModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [contentForm, setContentForm] = useState({
    category: "",
    subcategory: "",
    title: "",
    content: "",
    content_type: "text",
    style: "",
    platform: "",
    tags: [],
    is_active: true
  });
  
  const [resourceForm, setResourceForm] = useState({
    name: "",
    description: "",
    resource_type: "image",
    category: "promotional",
    file_url: "",
    thumbnail_url: "",
    is_active: true
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [catRes, contentRes, resourceRes, statsRes] = await Promise.all([
        axios.get(`${API}/marketing/categories`),
        axios.get(`${API}/marketing/content`),
        axios.get(`${API}/marketing/resources`),
        axios.get(`${API}/marketing/admin/stats`)
      ]);
      
      setCategories(catRes.data.content_categories || {});
      setResourceCategories(catRes.data.resource_categories || {});
      setContent(contentRes.data.content || []);
      setResources(resourceRes.data.resources || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async () => {
    if (!contentForm.category || !contentForm.title || !contentForm.content) {
      toast.error("Completa los campos requeridos");
      return;
    }
    
    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/marketing/content/${editingItem.id}`, contentForm);
        toast.success("Contenido actualizado");
      } else {
        await axios.post(`${API}/marketing/content`, contentForm);
        toast.success("Contenido creado");
      }
      
      setShowContentModal(false);
      setEditingItem(null);
      resetContentForm();
      fetchAll();
    } catch (error) {
      toast.error("Error al guardar contenido");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveResource = async () => {
    if (!resourceForm.name || !resourceForm.file_url) {
      toast.error("Completa los campos requeridos");
      return;
    }
    
    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/marketing/resources/${editingItem.id}`, {
          name: resourceForm.name,
          description: resourceForm.description,
          is_active: resourceForm.is_active
        });
        toast.success("Recurso actualizado");
      } else {
        await axios.post(`${API}/marketing/resources`, resourceForm);
        toast.success("Recurso creado");
      }
      
      setShowResourceModal(false);
      setEditingItem(null);
      resetResourceForm();
      fetchAll();
    } catch (error) {
      toast.error("Error al guardar recurso");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContent = async (id) => {
    if (!confirm("¿Eliminar este contenido?")) return;
    
    try {
      await axios.delete(`${API}/marketing/content/${id}`);
      toast.success("Contenido eliminado");
      fetchAll();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleDeleteResource = async (id) => {
    if (!confirm("¿Eliminar este recurso?")) return;
    
    try {
      await axios.delete(`${API}/marketing/resources/${id}`);
      toast.success("Recurso eliminado");
      fetchAll();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openEditContent = (item) => {
    setEditingItem(item);
    setContentForm({
      category: item.category || "",
      subcategory: item.subcategory || "",
      title: item.title || "",
      content: item.content || "",
      content_type: item.content_type || "text",
      style: item.style || "",
      platform: item.platform || "",
      tags: item.tags || [],
      is_active: item.is_active !== false
    });
    setShowContentModal(true);
  };

  const openEditResource = (item) => {
    setEditingItem(item);
    setResourceForm({
      name: item.name || "",
      description: item.description || "",
      resource_type: item.resource_type || "image",
      category: item.category || "promotional",
      file_url: item.file_url || "",
      thumbnail_url: item.thumbnail_url || "",
      is_active: item.is_active !== false
    });
    setShowResourceModal(true);
  };

  const resetContentForm = () => {
    setContentForm({
      category: "",
      subcategory: "",
      title: "",
      content: "",
      content_type: "text",
      style: "",
      platform: "",
      tags: [],
      is_active: true
    });
  };

  const resetResourceForm = () => {
    setResourceForm({
      name: "",
      description: "",
      resource_type: "image",
      category: "promotional",
      file_url: "",
      thumbnail_url: "",
      is_active: true
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-navy-900">
              Administrar Marketing
            </h1>
            <p className="text-navy-500">Gestiona contenido y recursos para vendedores</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-navy-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-900">{stats.total_content}</p>
                  <p className="text-sm text-navy-500">Contenidos</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-navy-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Image className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-900">{stats.total_resources}</p>
                  <p className="text-sm text-navy-500">Recursos</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-navy-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-900">{stats.total_ai_generations}</p>
                  <p className="text-sm text-navy-500">Generaciones IA</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-navy-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-900">{stats.total_shares}</p>
                  <p className="text-sm text-navy-500">Compartidos</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="content">Contenido</TabsTrigger>
            <TabsTrigger value="resources">Recursos</TabsTrigger>
          </TabsList>

          {/* CONTENT TAB */}
          <TabsContent value="content">
            <div className="bg-white rounded-xl border border-navy-100">
              <div className="p-4 border-b border-navy-100 flex items-center justify-between">
                <h3 className="font-semibold text-navy-900">Biblioteca de Contenido</h3>
                <Button
                  onClick={() => {
                    setEditingItem(null);
                    resetContentForm();
                    setShowContentModal(true);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Contenido
                </Button>
              </div>
              
              <div className="divide-y divide-navy-100">
                {content.length === 0 ? (
                  <div className="p-8 text-center text-navy-500">
                    No hay contenido. Agrega el primero.
                  </div>
                ) : (
                  content.map((item) => (
                    <div key={item.id} className="p-4 flex items-center justify-between hover:bg-navy-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-navy-900">{item.title}</h4>
                          <span className="text-xs px-2 py-0.5 bg-navy-100 rounded-full text-navy-600">
                            {item.category}
                          </span>
                          {item.subcategory && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 rounded-full text-blue-600">
                              {item.subcategory}
                            </span>
                          )}
                          {!item.is_active && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 rounded-full text-red-600">
                              Inactivo
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-navy-500 line-clamp-1">{item.content}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditContent(item)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteContent(item.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* RESOURCES TAB */}
          <TabsContent value="resources">
            <div className="bg-white rounded-xl border border-navy-100">
              <div className="p-4 border-b border-navy-100 flex items-center justify-between">
                <h3 className="font-semibold text-navy-900">Recursos Visuales</h3>
                <Button
                  onClick={() => {
                    setEditingItem(null);
                    resetResourceForm();
                    setShowResourceModal(true);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Recurso
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                {resources.length === 0 ? (
                  <div className="col-span-full p-8 text-center text-navy-500">
                    No hay recursos. Agrega el primero.
                  </div>
                ) : (
                  resources.map((item) => (
                    <div key={item.id} className="border border-navy-100 rounded-lg overflow-hidden">
                      <div className="aspect-video bg-navy-100 relative">
                        {item.thumbnail_url || item.file_url ? (
                          <img
                            src={item.thumbnail_url || item.file_url}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-navy-300" />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h4 className="font-medium text-sm text-navy-900 truncate">{item.name}</h4>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-navy-500">{item.resource_type}</span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => openEditResource(item)}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600"
                              onClick={() => handleDeleteResource(item.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* CONTENT MODAL */}
        <Dialog open={showContentModal} onOpenChange={setShowContentModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "Editar Contenido" : "Agregar Contenido"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Categoría *</label>
                  <Select
                    value={contentForm.category}
                    onValueChange={(v) => setContentForm({ ...contentForm, category: v, subcategory: "" })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(categories).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subcategoría</label>
                  <Select
                    value={contentForm.subcategory}
                    onValueChange={(v) => setContentForm({ ...contentForm, subcategory: v })}
                    disabled={!contentForm.category}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona subcategoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {contentForm.category && categories[contentForm.category]?.subcategories &&
                        Object.entries(categories[contentForm.category].subcategories).map(([key, name]) => (
                          <SelectItem key={key} value={key}>{name}</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Título *</label>
                <Input
                  value={contentForm.title}
                  onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                  placeholder="Título del contenido"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Contenido *</label>
                <Textarea
                  value={contentForm.content}
                  onChange={(e) => setContentForm({ ...contentForm, content: e.target.value })}
                  placeholder="Escribe el contenido aquí..."
                  rows={6}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Estilo</label>
                  <Select
                    value={contentForm.style}
                    onValueChange={(v) => setContentForm({ ...contentForm, style: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona estilo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Profesional</SelectItem>
                      <SelectItem value="motivational">Motivacional</SelectItem>
                      <SelectItem value="educational">Educativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={contentForm.is_active}
                      onChange={(e) => setContentForm({ ...contentForm, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Activo</span>
                  </label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowContentModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveContent} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* RESOURCE MODAL */}
        <Dialog open={showResourceModal} onOpenChange={setShowResourceModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "Editar Recurso" : "Agregar Recurso"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre *</label>
                <Input
                  value={resourceForm.name}
                  onChange={(e) => setResourceForm({ ...resourceForm, name: e.target.value })}
                  placeholder="Nombre del recurso"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Descripción</label>
                <Input
                  value={resourceForm.description}
                  onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                  placeholder="Descripción opcional"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo</label>
                  <Select
                    value={resourceForm.resource_type}
                    onValueChange={(v) => setResourceForm({ ...resourceForm, resource_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Imagen</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="template">Plantilla</SelectItem>
                      <SelectItem value="banner">Banner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Categoría</label>
                  <Select
                    value={resourceForm.category}
                    onValueChange={(v) => setResourceForm({ ...resourceForm, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(resourceCategories).map(([key, name]) => (
                        <SelectItem key={key} value={key}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">URL del archivo *</label>
                <Input
                  value={resourceForm.file_url}
                  onChange={(e) => setResourceForm({ ...resourceForm, file_url: e.target.value })}
                  placeholder="https://..."
                />
                <p className="text-xs text-navy-500 mt-1">
                  Sube el archivo a Cloudinary y pega la URL aquí
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">URL de miniatura</label>
                <Input
                  value={resourceForm.thumbnail_url}
                  onChange={(e) => setResourceForm({ ...resourceForm, thumbnail_url: e.target.value })}
                  placeholder="https://... (opcional)"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resourceForm.is_active}
                  onChange={(e) => setResourceForm({ ...resourceForm, is_active: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Activo</span>
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResourceModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveResource} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminMarketingPanel;
